/**
 * Deterministic random number generation.
 *
 * The simulation must produce identical results in a browser at 60fps with a
 * human watching and on a server at 3am with nobody watching. That requires
 * every stochastic decision to flow from an injected, seeded, serializable
 * generator rather than from `Math.random()`.
 *
 * See `docs/determinism.md` for the rules this file exists to enforce.
 */

/**
 * The complete internal state of an `Rng`.
 *
 * Four 32-bit words. Capturing this is necessary and sufficient to resume a
 * generator mid-stream, which is what makes snapshots total.
 */
export interface RngState {
  a: number;
  b: number;
  c: number;
  d: number;
}

/**
 * A seeded source of randomness.
 *
 * Every method that draws consumes a well-defined number of steps from the
 * stream, so two runs that make the same sequence of calls see the same
 * sequence of values.
 */
export interface Rng {
  /** Uniform in [0, 1). Consumes one step. */
  next(): number;

  /** Uniform integer in [0, n). Returns 0 for n <= 0. Consumes one step. */
  int(n: number): number;

  /** Uniform in [min, max). Consumes one step. */
  range(min: number, max: number): number;

  /** Uniform element of `arr`, or `undefined` if empty. Consumes one step. */
  pick<T>(arr: readonly T[]): T | undefined;

  /** True with probability `p`. Consumes one step. */
  chance(p: number): boolean;

  /**
   * Derive an independent named sub-stream.
   *
   * The child is a pure function of this generator's *seed lineage* and the
   * label, not of how many values this generator has drawn. Two colonies can
   * therefore consume randomness at different rates without perturbing each
   * other. Does not consume a step.
   */
  fork(label: string): Rng;

  /** Capture state for a snapshot. */
  save(): RngState;

  /** Resume from a captured state. */
  load(state: RngState): void;
}

/**
 * xmur3 string hash — expands an arbitrary seed into well-mixed 32-bit words.
 *
 * A poor seed expansion is the classic way a "seeded" generator ends up
 * correlated across streams, so this is deliberately not `state = seed`.
 */
function xmur3(str: string): () => number {
  let h = 1779033703 ^ str.length;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  return () => {
    h = Math.imul(h ^ (h >>> 16), 2246822507);
    h = Math.imul(h ^ (h >>> 13), 3266489909);
    h ^= h >>> 16;
    return h >>> 0;
  };
}

/**
 * sfc32 — "Small Fast Counter", 128-bit state.
 *
 * Chosen because every operation is integer-only (`Math.imul`, shifts, `| 0`).
 * There is no floating-point accumulation in the state, so the stream is
 * bit-identical on any JavaScript engine rather than merely on V8.
 */
export class Sfc32Rng implements Rng {
  private a = 0;
  private b = 0;
  private c = 0;
  private d = 0;

  /** The seed string this generator was built from, used to derive forks. */
  private readonly lineage: string;

  constructor(seed: string | number) {
    this.lineage = String(seed);
    const h = xmur3(this.lineage);
    this.a = h();
    this.b = h();
    this.c = h();
    this.d = h();
    // Discard the first few outputs; sfc32 needs a short warm-up before its
    // output is well distributed.
    for (let i = 0; i < 12; i++) this.next();
  }

  next(): number {
    const a = this.a;
    const b = this.b;
    const c = this.c;
    const d = this.d;
    const t = (((a + b) | 0) + d) | 0;
    this.d = (d + 1) | 0;
    this.a = b ^ (b >>> 9);
    this.b = (c + (c << 3)) | 0;
    this.c = (c << 21) | (c >>> 11);
    this.c = (this.c + t) | 0;
    return (t >>> 0) / 4294967296;
  }

  int(n: number): number {
    if (!(n > 0)) {
      // Still consume a step: callers must not be able to change the stream
      // by passing a degenerate bound.
      this.next();
      return 0;
    }
    return Math.floor(this.next() * n);
  }

  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  pick<T>(arr: readonly T[]): T | undefined {
    if (arr.length === 0) {
      this.next();
      return undefined;
    }
    return arr[Math.floor(this.next() * arr.length)];
  }

  chance(p: number): boolean {
    return this.next() < p;
  }

  fork(label: string): Rng {
    return new Sfc32Rng(`${this.lineage}/${label}`);
  }

  save(): RngState {
    return { a: this.a, b: this.b, c: this.c, d: this.d };
  }

  load(state: RngState): void {
    this.a = state.a | 0;
    this.b = state.b | 0;
    this.c = state.c | 0;
    this.d = state.d | 0;
  }
}

/**
 * Build the canonical seed string for a district simulation.
 *
 * Seeds are structured rather than opaque so that the same world and district
 * replay identically season after season, and so that a bug report can carry
 * its seed as three readable fields.
 */
export function districtSeed(world: string, district: string, season: number): string {
  return `${world}:${district}:${season}`;
}

/** Convenience constructor, so callers need not name the implementation. */
export function createRng(seed: string | number): Rng {
  return new Sfc32Rng(seed);
}
